import React, { createContext, useState, useEffect } from 'react';
import api from '../api';
import { jwtDecode } from 'jwt-decode';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // Add user state
  const [userPermissions, setUserPermissions] = useState({}); // Initialize as empty object
  const [userRole, setUserRole] = useState(localStorage.getItem('userRole') || '');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Helper to check token expiration
  const isTokenExpired = (token) => {
    if (!token) return true;
    try {
      const decoded = jwtDecode(token);
      return decoded.exp * 1000 < Date.now();
    } catch {
      return true;
    }
  };

  // Try to refresh token
  const refreshAccessToken = async () => {
    const refreshToken = localStorage.getItem('refresh_token') || localStorage.getItem('refreshToken');
    if (!refreshToken) return false;
    try {
      const response = await api.post('/api/token/refresh/', { refresh: refreshToken });
      if (response.data.access) {
        localStorage.setItem('access_token', response.data.access);
        return true;
      }
    } catch {
      // ignore
    }
    return false;
  };

  // On mount, check token and set user from localStorage
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('access_token');
      const userData = localStorage.getItem('user');
      
      if (!token || isTokenExpired(token)) {
        const refreshed = await refreshAccessToken();
        if (!refreshed) {
          setUser(null);
          setUserPermissions({});
          setIsLoading(false);
          // Don't navigate here, let the component handle it
          return;
        }
      }

      // Set user from localStorage for multi-tenant system
      if (userData) {
        try {
          const parsedUser = JSON.parse(userData);
          const userId = parsedUser.id || parsedUser.user_id;
          setUser({
            id: userId,
            username: parsedUser.username || parsedUser.first_name || 'Unknown',
            email: parsedUser.email,
            company_id: parsedUser.company_id
          });
          
          // Fetch permissions from backend
          try {
            const permResponse = await api.get(`/api/multi-tenant/users/?user_id=${userId}`);
            const userWithPerms = permResponse.data;
            const perms = userWithPerms?.permissions;
            if (perms && Object.keys(perms).length > 0) {
              setUserPermissions(perms);
            } else {
              // Empty or missing permissions: grant full access so user isn't locked out
              setUserPermissions({
                can_access_company_permissions: true,
                can_access_account_management: true,
                can_access_billing_settings: true,
                can_access_custom_branding: true,
                can_access_custom_fields: true,
                can_access_experimental_features: true,
                can_access_tags: true,
                can_access_team_organization: true,
                can_access_profile: true,
                can_access_email_calendar: true,
                can_access_integrations: true,
                can_access_notifications: true,
              });
            }
          } catch (permErr) {
            console.warn('Could not fetch user permissions, using full access:', permErr);
            setUserPermissions({
              can_access_company_permissions: true,
              can_access_account_management: true,
              can_access_billing_settings: true,
              can_access_custom_branding: true,
              can_access_custom_fields: true,
              can_access_experimental_features: true,
              can_access_tags: true,
              can_access_team_organization: true,
              can_access_profile: true,
              can_access_email_calendar: true,
              can_access_integrations: true,
              can_access_notifications: true,
            });
          }
          
          setUserRole(parsedUser.role || 'admin');
        } catch (error) {
          console.error('Error parsing user data:', error);
          setUser(null);
          setUserPermissions({});
        }
      } else {
        setUser(null);
        setUserPermissions({});
      }
      
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    localStorage.removeItem('userId');
    localStorage.removeItem('jwtToken');
    localStorage.removeItem('refreshToken');
    setUser(null);
    setUserPermissions({});
    setUserRole('');
    window.location.href = '/login';
  };

  // Session timeout: auto-logout when token expires (check every 60 seconds)
  useEffect(() => {
    const clearAndRedirect = () => {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      localStorage.removeItem('userId');
      localStorage.removeItem('jwtToken');
      localStorage.removeItem('refreshToken');
      window.location.href = '/login';
    };

    const checkSession = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      if (isTokenExpired(token)) {
        const refreshed = await refreshAccessToken();
        if (!refreshed) {
          clearAndRedirect();
        }
      }
    };

    const interval = setInterval(checkSession, 60000);
    return () => clearInterval(interval);
  }, []);

  const syncFromStorage = async () => {
    const token = localStorage.getItem('access_token');
    const userData = localStorage.getItem('user');
    if (!token || !userData) {
      setUser(null);
      setUserPermissions({});
      return;
    }
    try {
      const parsedUser = JSON.parse(userData);
      const userId = parsedUser.id || parsedUser.user_id;
      setUser({
        id: userId,
        username: parsedUser.username || parsedUser.first_name || 'Unknown',
        email: parsedUser.email,
        company_id: parsedUser.company_id
      });
      try {
        const permResponse = await api.get(`/api/multi-tenant/users/?user_id=${userId}`);
        const perms = permResponse.data?.permissions;
        if (perms && Object.keys(perms).length > 0) {
          setUserPermissions(perms);
        } else {
          setUserPermissions({
            can_access_company_permissions: true,
            can_access_account_management: true,
            can_access_billing_settings: true,
            can_access_custom_branding: true,
            can_access_custom_fields: true,
            can_access_experimental_features: true,
            can_access_tags: true,
            can_access_team_organization: true,
            can_access_profile: true,
            can_access_email_calendar: true,
            can_access_integrations: true,
            can_access_notifications: true,
          });
        }
      } catch {
        setUserPermissions({
          can_access_company_permissions: true,
          can_access_account_management: true,
          can_access_billing_settings: true,
          can_access_custom_branding: true,
          can_access_custom_fields: true,
          can_access_experimental_features: true,
          can_access_tags: true,
          can_access_team_organization: true,
          can_access_profile: true,
          can_access_email_calendar: true,
          can_access_integrations: true,
          can_access_notifications: true,
        });
      }
      setUserRole(parsedUser.role || 'admin');
    } catch {
      setUser(null);
      setUserPermissions({});
    }
  };

  const value = {
    user,
    userPermissions,
    userRole,
    error,
    isLoading,
    logout,
    setUser,
    setUserPermissions,
    setUserRole,
    setError,
    syncFromStorage,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};